import { useEffect, useRef, useState, useCallback } from "react";
import "@/App.css";
import Lenis from "lenis";
import { Toaster } from "sonner";

import { Loader } from "@/components/Loader";
import { NavDock } from "@/components/NavDock";
import { Hero } from "@/components/Hero";
import { Marquee } from "@/components/Marquee";
import { About } from "@/components/About";
import { Work } from "@/components/Work";
import { Decks } from "@/components/Decks";
import { VideoPanel } from "@/components/VideoPanel";
import { Process } from "@/components/Process";
import { Skills } from "@/components/Skills";
import { Contact } from "@/components/Contact";
import { Footer } from "@/components/Footer";
import { Gate, GATE_TOKEN_KEY } from "@/components/Gate";
import axios from "axios";

const MARQUEE = [
  "Digital Apparel", "CLO 3D", "Licensed Innovation", "Technical Design", "AI-Assisted Concept Creation", "Commercial Product Development",
];

function App() {
  const lenisRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const [splineLoaded, setSplineLoaded] = useState(false);
  const [done, setDone] = useState(false);
  const [active, setActive] = useState("home");
  const [unlocked, setUnlocked] = useState(false);
  const [gateChecked, setGateChecked] = useState(false);

  // Verify any existing gate token on load
  useEffect(() => {
    const token = sessionStorage.getItem(GATE_TOKEN_KEY);
    if (!token) { setGateChecked(true); return; }
    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/api/gate/check`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => setUnlocked(Boolean(data?.valid)))
      .catch(() => setUnlocked(false))
      .finally(() => setGateChecked(true));
  }, []);

  // Lenis smooth scroll
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    lenisRef.current = lenis;
    let raf;
    const loop = (t) => { lenis.raf(t); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); };
  }, []);

  // Loading progress: creep to 92, then jump to 100 once Spline loads (or timeout)
  useEffect(() => {
    const id = setInterval(() => {
      setProgress((p) => (p < 92 ? p + Math.random() * 6 : p));
    }, 180);
    const fallback = setTimeout(() => setSplineLoaded(true), 7000);
    return () => { clearInterval(id); clearTimeout(fallback); };
  }, []);

  useEffect(() => {
    if (!splineLoaded) return;
    setProgress(100);
    const t = setTimeout(() => setDone(true), 700);
    return () => clearTimeout(t);
  }, [splineLoaded]);

  // Lock scroll while loading
  useEffect(() => {
    if (lenisRef.current) done ? lenisRef.current.start() : lenisRef.current.stop();
    document.body.style.overflow = done ? "" : "hidden";
  }, [done]);

  // Active section tracking
  useEffect(() => {
    const ids = ["home", "about", "work", "decks", "process", "skills", "contact"];
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && setActive(e.target.id)),
      { rootMargin: "-45% 0px -45% 0px" }
    );
    ids.forEach((id) => { const el = document.getElementById(id); if (el) obs.observe(el); });
    return () => obs.disconnect();
  }, [done]);

  const navigate = useCallback((id) => {
    const el = document.getElementById(id);
    if (el && lenisRef.current) lenisRef.current.scrollTo(el, { offset: -10 });
  }, []);

  const toTop = useCallback(() => lenisRef.current?.scrollTo(0), []);

  if (!unlocked) {
    return (
      <div className="App grain relative min-h-screen bg-ink">
        <Toaster theme="dark" position="top-center" />
        {gateChecked && <Gate onUnlock={() => setUnlocked(true)} />}
      </div>
    );
  }

  return (
    <div className="App grain relative bg-ink">
      <Loader progress={progress} done={done} />
      <Toaster theme="dark" position="top-center" />

      <NavDock active={active} onNavigate={navigate} />

      <Hero onSplineLoad={() => setSplineLoaded(true)} onNavigate={navigate} started={done} />

      <div className="border-y border-white/10 py-6">
        <Marquee items={MARQUEE} />
      </div>

      <About />
      <Work />
      <Decks />
      <VideoPanel />
      <Process />
      <Skills />
      <Contact />
      <Footer onTop={toTop} />
    </div>
  );
}

export default App;
